/**
 * WelcomeState - Shown in ChatPanel when no messages exist.
 * Displays suggested prompts as clickable chips.
 */

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';

interface WelcomeStateProps {
  onPromptClick: (prompt: string) => void;
  prompts?: string[];
}

export default function WelcomeState({ onPromptClick, prompts = [] }: WelcomeStateProps) {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 4,
        px: 2,
      }}
    >
      <AutoAwesomeRounded
        sx={(t) => ({
          fontSize: 36,
          color: t.tokens.brand.primary,
          opacity: 0.7,
        })}
      />
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, fontSize: '0.9rem', textAlign: 'center' }}
      >
        Data Copilot
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: '0.8rem', textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}
      >
        Ask questions about your data. I'll explore the schema, write SQL, and analyze results.
      </Typography>

      {prompts.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            mt: 1,
            width: '100%',
            maxWidth: 320,
          }}
        >
          {prompts.map((prompt) => (
            <Chip
              key={prompt}
              label={prompt}
              variant="outlined"
              onClick={() => onPromptClick(prompt)}
              sx={(t) => ({
                height: 'auto',
                py: 0.75,
                px: 0.5,
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
                borderColor: t.tokens.border.default,
                color: t.tokens.text.primary,
                bgcolor: t.tokens.surface.raised,
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  lineHeight: 1.4,
                },
                '&:hover': {
                  bgcolor: t.tokens.brand.primarySoft,
                  borderColor: t.tokens.brand.primary,
                  color: t.tokens.brand.primary,
                },
                transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
              })}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
